import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Renders a set of "look" products side by side (desktop: 3 columns, mobile: stacked),
 * keeps each product's photo in sync with its own color/size selection, and adds all
 * selected variants to the cart in a single click via one shared button.
 */
class LookProductsComponent extends HTMLElement {
  connectedCallback() {
    this.products = Array.from(this.querySelectorAll('[data-look-product]'));
    this.products.forEach((productEl) => this.#initProduct(productEl));

    this.addAllButton = this.querySelector('[data-look-add-all]');
    this.errorEl = this.querySelector('[data-look-error]');
    this.addAllButton?.addEventListener('click', this.#handleAddAll);
  }

  #initProduct(productEl) {
    const script = productEl.querySelector('script[data-look-product-variants]');
    if (!script) return;

    let variants = [];
    try {
      variants = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[look-products] Failed to parse product variants JSON', error);
      return;
    }

    const selects = Array.from(productEl.querySelectorAll('[data-look-option]'));
    const sync = () => this.#syncVariant(productEl, selects, variants);
    selects.forEach((select) => select.addEventListener('change', sync));

    sync();
  }

  #syncVariant(productEl, selects, variants) {
    const orderedSelects = [...selects].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
    const optionValues = orderedSelects.map((select) => select.value);

    const matched = variants.find((variant) =>
      (variant.options || []).every((value, index) => value === optionValues[index])
    );

    const imageEl = productEl.querySelector('[data-look-product-image]');
    const priceEl = productEl.querySelector('[data-look-product-price]');
    const unavailableEl = productEl.querySelector('[data-look-product-unavailable]');

    if (!matched) {
      productEl.dataset.selectedVariantId = '';
      productEl.dataset.variantAvailable = 'false';
      if (unavailableEl) unavailableEl.hidden = false;
      return;
    }

    productEl.dataset.selectedVariantId = String(matched.id);
    productEl.dataset.variantAvailable = matched.available ? 'true' : 'false';
    if (unavailableEl) unavailableEl.hidden = !!matched.available;

    if (priceEl && typeof matched.price === 'number') {
      priceEl.textContent = this.#formatMoney(matched.price);
    }

    const imageSrc = matched.featured_image && matched.featured_image.src;
    if (imageEl && imageSrc) {
      try {
        const url = new URL(imageSrc, window.location.origin);
        url.searchParams.set('width', '800');
        imageEl.removeAttribute('srcset');
        imageEl.src = url.toString();
      } catch (error) {
        imageEl.removeAttribute('srcset');
        imageEl.src = imageSrc;
      }
    }
  }

  #formatMoney(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'UAH';
      return new Intl.NumberFormat(document.documentElement.lang || 'uk-UA', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  #handleAddAll = async () => {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    const items = [];
    for (const productEl of this.products) {
      const variantId = productEl.dataset.selectedVariantId;
      const available = productEl.dataset.variantAvailable === 'true';
      if (!variantId || !available) {
        this.#showError('Оберіть колір і розмір для кожного товару.');
        return;
      }
      items.push({ id: Number(variantId), quantity: 1 });
    }

    if (items.length === 0) return;

    if (this.addAllButton) this.addAllButton.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
    const cartUrl = this.dataset.cartUrl || '/cart';

    let deferredEventPromise = null;
    try {
      deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferredEventPromise.promise,
        })
      );
    } catch (error) {
      deferredEventPromise = null;
    }

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || 'Не вдалося додати товари в кошик.';
        deferredEventPromise?.reject(new Error(message));
        this.#showError(message);
        return;
      }

      if (deferredEventPromise) {
        let cart = data;
        try {
          const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          cart = await cartResponse.json();
        } catch (error) {
          // Keep the /cart/add.js response as a fallback cart snapshot.
        }

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse
            ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
            : cart,
          detail: {
            items: data.items,
            source: 'look-products-component',
            sourceId: this.dataset.sectionId,
            itemCount: items.length,
            sections: data.sections,
            didError: false,
          },
        });
      }

      // Auto-apply the outfit bundle discount code — silently sets the checkout
      // discount cookie via GET, no redirect needed since it's same-origin.
      try {
        await fetch('/discount/OUTFIT10', { credentials: 'same-origin' });
      } catch (error) {
        // Non-fatal: worst case the customer enters the code manually at checkout.
      }

      // The store's global "auto-open cart drawer" setting is off, so the native
      // CartLinesUpdateEvent listener won't open it on its own — open it explicitly
      // here so the add-all button always shows the result. Skip if it's already open.
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer && typeof drawer.open === 'function') {
        if (!drawer.hasAttribute('open')) {
          drawer.open();
        }
      } else {
        window.location.href = cartUrl;
      }
    } catch (error) {
      console.warn('[look-products] Add all to cart failed', error);
      deferredEventPromise?.reject(error);
      this.#showError('Не вдалося додати товари в кошик. Спробуйте ще раз.');
    } finally {
      if (this.addAllButton) this.addAllButton.disabled = false;
    }
  };

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }
}

if (!customElements.get('look-products-component')) {
  customElements.define('look-products-component', LookProductsComponent);
}
